import { Spin } from "antd";
import { giveAwayContent } from "../../../../utils/giveaAwayContent";
import { useLanguage } from "../../../../context/LanguageContext";

// components/winners/EmailDialog.jsx
export function EmailDialog({
  open,
  onCancel,
  onSend,
  winner,
  emailTemplate,
  setEmailTemplate,
  loading = false,
}) {
  const { language } = useLanguage();
  const t = giveAwayContent[language].emailDialog;
  const normalizedWinner = winner
    ? {
        participantName:
          `${winner.winner?.firstname || ""} ${
            winner.winner?.lastname || ""
          }`.trim() || t.unknownParticipant,
        participantEmail: winner.winner?.email || t.noEmail,
        giveawayTitle: winner.giveawayTitle || t.unknownGiveaway,
        prize: {
          title: winner.prize?.title || t.unknownPrize,
          description: winner.prize?.description || "",
          value: winner.prize?.value || t.unknownValue,
          voucherCode: winner.prize?.voucherCode || "",
        },
      }
    : null;

  const copyVoucherCode = (code) => {
    if (navigator.clipboard) {
      navigator.clipboard
        .writeText(code)
        .then(() => {
          alert(t.copySuccess);
        })
        .catch(() => {
          fallbackCopyTextToClipboard(code);
        });
    } else {
      fallbackCopyTextToClipboard(code);
    }
  };

  const fallbackCopyTextToClipboard = (text) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.top = "0";
    textArea.style.left = "0";
    textArea.style.position = "fixed";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      document.execCommand("copy");
      alert(t.copySuccess);
    } catch (err) {
      alert(t.copyError + text);
    }

    document.body.removeChild(textArea);
  };

  // Validierung der Email-Vorlage
  const isEmailValid = emailTemplate.trim().length > 0;

  if (!open || !winner || !normalizedWinner) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold">{t.title}</h2>
          {loading && (
            <div className="flex items-center gap-2 text-orange-600">
              <Spin size="small" />
              <span className="text-sm">{t.sendingStatus}</span>
            </div>
          )}
        </div>

        <p className="text-gray-600 mb-4">
          {t.sendToText} <strong>{normalizedWinner.participantName}</strong>
        </p>

        {/* Winner Details Card */}
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
          <h3 className="font-semibold text-orange-800 mb-2 flex items-center gap-2">
            {t.winnerDetailsTitle}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-orange-700">
            <div>
              <p>
                <strong>{t.fields.name}</strong>{" "}
                {normalizedWinner.participantName}
              </p>
              <p>
                <strong>{t.fields.email}</strong>{" "}
                {normalizedWinner.participantEmail}
              </p>
            </div>
            <div>
              <p>
                <strong>{t.fields.giveaway}</strong>{" "}
                {normalizedWinner.giveawayTitle}
              </p>
              <p>
                <strong>{t.fields.prize}</strong>{" "}
                {normalizedWinner.prize.description} (
                {normalizedWinner.prize.value})
              </p>
            </div>
          </div>

          {/* Voucher Code Section */}
          {normalizedWinner.prize.voucherCode &&
            normalizedWinner.prize.voucherCode.trim() !== "" && (
              <div className="mt-3 pt-3 border-t border-orange-200">
                <div className="flex items-center gap-2 flex-wrap">
                  <strong className="text-orange-800">
                    {t.voucherCodeLabel}
                  </strong>
                  <code className="bg-white px-3 py-1 rounded border font-mono text-sm">
                    {normalizedWinner.prize.voucherCode}
                  </code>
                  <button
                    onClick={() =>
                      copyVoucherCode(normalizedWinner.prize.voucherCode)
                    }
                    disabled={loading}
                    className="text-xs bg-orange-200 px-3 py-1 rounded hover:bg-orange-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {t.copyButton}
                  </button>
                </div>
              </div>
            )}

          {/* Warnung falls kein Voucher Code vorhanden */}
          {(!normalizedWinner.prize.voucherCode ||
            normalizedWinner.prize.voucherCode.trim() === "") && (
            <div className="mt-3 pt-3 border-t border-orange-200">
              <div className="flex items-center gap-2 text-amber-600 bg-amber-50 p-2 rounded">
                <span>{t.noVoucherWarning}</span>
                <span className="text-sm">
                  <strong>{t.noVoucherNote}</strong> {t.noVoucherText}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Email Template Section */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium">
              {t.emailTemplateLabel}
            </label>
            <span className="text-xs text-gray-500">
              {emailTemplate.length} {t.charactersCount}
            </span>
          </div>

          <textarea
            value={emailTemplate}
            onChange={(e) => setEmailTemplate(e.target.value)}
            disabled={loading}
            className="w-full border border-gray-300 rounded-md px-3 py-2 font-mono text-sm disabled:bg-gray-100 disabled:cursor-not-allowed focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            rows={12}
            placeholder={t.emailPlaceholder}
          />

          {/* Email Validation */}
          {!isEmailValid && (
            <p className="text-red-500 text-xs mt-1">
              {t.emailValidationError}
            </p>
          )}
        </div>

        {/* Quick Template Buttons */}
        <div className="mb-6">
          <label className="block text-sm font-medium mb-2">
            {t.quickTemplatesLabel}
          </label>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() =>
                setEmailTemplate(
                  t.standardTemplateText(
                    normalizedWinner.participantName,
                    normalizedWinner.giveawayTitle,
                    normalizedWinner.prize.description,
                    normalizedWinner.prize.value,
                    normalizedWinner.prize.voucherCode
                  )
                )
              }
              disabled={loading}
              className="px-3 py-1 bg-gray-100 text-gray-700 rounded text-xs hover:bg-gray-200 disabled:opacity-50"
            >
              {t.standardTemplate}
            </button>

            <button
              onClick={() =>
                setEmailTemplate(
                  t.festiveTemplateText(
                    normalizedWinner.participantName,
                    normalizedWinner.giveawayTitle,
                    normalizedWinner.prize.description,
                    normalizedWinner.prize.value,
                    normalizedWinner.prize.voucherCode
                  )
                )
              }
              disabled={loading}
              className="px-3 py-1 bg-blue-100 text-blue-700 rounded text-xs hover:bg-blue-200 disabled:opacity-50"
            >
              {t.festiveTemplate}
            </button>
          </div>
        </div>

        {/* Preview Section */}
        <div className="mb-6">
          <label className="block text-sm font-medium mb-2">
            {t.previewTitle}
          </label>
          <div className="border border-gray-200 rounded-md p-3 bg-gray-50 max-h-32 overflow-y-auto">
            <div className="text-xs text-gray-600 mb-2">
              <strong>{t.previewTo}</strong> {normalizedWinner.participantEmail}
              <br />
              <strong>{t.previewSubject}</strong> {t.previewSubjectText}
            </div>
            <div className="text-sm whitespace-pre-wrap">
              {emailTemplate || t.previewNoMessage}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            disabled={loading}
            className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {t.cancelButton}
          </button>

          <button
            onClick={onSend}
            disabled={loading || !isEmailValid}
            className="px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
          >
            {loading ? (
              <>
                <Spin size="small" />
                {t.sendingButton}
              </>
            ) : (
              <>{t.sendButton}</>
            )}
          </button>
        </div>

        {/* Info Footer */}
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-xs text-blue-700">
            {t.infoTip} <strong>{t.infoTitle}</strong> {t.infoText}
          </p>
        </div>
      </div>
    </div>
  );
}
